import { useMemo, useRef, useState } from "react";

import {
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";

import { useOutletApi } from "@/features/authentication";
import { formatMoney, formatNumber, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle, useSnackbar } from "@/features/ui";

import {
  buildCostUpdatePayload,
  downloadCostUpdateTemplate,
  downloadErrorList,
  FILE_ACCEPT,
  FileReadError,
  mergeCostUpdates,
  parseCostUpdateRows,
  readSheetRows,
} from "../stock-adjustment-file";

const GRID_HEIGHT = 380;
const SHOWN_NOT_FOUND = 15;

const columns = [
  { field: "sku", headerName: "SKU", flex: 1, minWidth: 140 },
  { field: "name", headerName: "Nama", flex: 2, minWidth: 200 },
  {
    field: "quantity",
    headerName: "Qty",
    flex: 0.5,
    minWidth: 80,
    type: "number",
    valueFormatter: (value) => formatNumber(value),
  },
  {
    field: "oldCost",
    headerName: "Harga Lama",
    flex: 1,
    minWidth: 130,
    type: "number",
    valueFormatter: (value) => formatMoney(value),
  },
  {
    field: "newCost",
    headerName: "Harga Baru",
    flex: 1,
    minWidth: 130,
    type: "number",
    valueFormatter: (value) => formatMoney(value),
  },
  {
    field: "difference",
    headerName: "Selisih / Unit",
    flex: 1,
    minWidth: 130,
    type: "number",
    valueFormatter: (value) =>
      `${value > 0 ? "+" : ""}${formatMoney(value)}`,
  },
];

/**
 * "Update harga dari file" on an existing Penyesuaian Stok: a file of `SKU | Harga per Unit` is
 * merged with the document's current lines and the full line set is sent back through the normal
 * edit (PATCH replaces the whole set, so every line is resent, unchanged quantities and notes
 * included). Only the cost of the lines in the file changes.
 */
export const UpdateCostFromFileDialog = ({
  open,
  onClose,
  stockAdjustment,
  onUpdated,
}) => {
  const api = useOutletApi();
  const { showSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const savingRef = useRef(false);

  const documentLines = useMemo(
    () => stockAdjustment?.stockAdjustmentProductVariants ?? [],
    [stockAdjustment]
  );

  const [stage, setStage] = useState("pick"); // pick | reading | ready | saving
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState(null);
  const [fileErrors, setFileErrors] = useState([]);
  const [merged, setMerged] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const busy = stage === "reading" || stage === "saving";

  const resetState = () => {
    setStage("pick");
    setFileName("");
    setFileError(null);
    setFileErrors([]);
    setMerged(null);
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    resetState();
    onClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStage("reading");
    setFileName(file.name);
    setFileError(null);
    setFileErrors([]);
    setMerged(null);
    setSubmitError(null);

    try {
      const parsed = parseCostUpdateRows(await readSheetRows(file));
      setFileError(parsed.fileError);
      setFileErrors(parsed.errors);
      if (!parsed.fileError && parsed.errors.length === 0) {
        setMerged(mergeCostUpdates(documentLines, parsed.lines));
      }
    } catch (error) {
      setFileError(
        error instanceof FileReadError ? error.message : "Gagal membaca file"
      );
    } finally {
      setStage("ready");
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setStage("saving");
    setSubmitError(null);

    try {
      await api.patch(
        `stock-adjustments/${stockAdjustment.referenceNumber}`,
        buildCostUpdatePayload(stockAdjustment, documentLines, merged.changed)
      );
      showSnackbar(
        `Harga ${formatNumber(merged.changed.length)} baris berhasil diperbarui`,
        "success"
      );
      resetState();
      onUpdated();
    } catch (error) {
      const message = getErrorMessage(error, "Gagal memperbarui harga");
      setSubmitError(message);
      showSnackbar(message);
      setStage("ready");
    } finally {
      savingRef.current = false;
    }
  };

  const gridRows = useMemo(
    () =>
      (merged?.changed ?? []).map((entry) => ({
        id: entry.sku,
        ...entry,
        difference: Math.round((entry.newCost - entry.oldCost) * 100) / 100,
      })),
    [merged]
  );

  const handleDownloadErrors = () =>
    downloadErrorList(
      [
        ...fileErrors,
        ...(merged?.notFound ?? []).map(({ row, sku }) => ({
          row,
          sku,
          message: "SKU tidak ada di penyesuaian stok ini",
        })),
      ],
      "daftar-kesalahan-update-harga.xlsx"
    );

  const hasChanges = (merged?.changed.length ?? 0) > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={busy}
    >
      <CustomDialogTitle
        title="Update Harga dari File"
        onClose={busy ? undefined : handleClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Penyesuaian stok <strong>{stockAdjustment?.referenceNumber}</strong>
            . Unggah file dengan kolom <strong>SKU | Harga per Unit</strong>.
            Hanya harga baris penambahan stok yang berubah; jumlah dan catatan
            tetap, dan SKU yang tidak ada di file tidak diubah.
          </Typography>

          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Button
              component="label"
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={busy}
            >
              {fileName ? "Ganti File" : "Pilih File (.xlsx / .csv)"}
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={FILE_ACCEPT}
                onChange={handleFileChange}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadCostUpdateTemplate}
              disabled={busy}
            >
              Unduh Template
            </Button>
            {fileName && (
              <Typography variant="body2" color="text.secondary">
                {fileName}
              </Typography>
            )}
          </Stack>

          {stage === "reading" && <Alert severity="info">Membaca file…</Alert>}
          {fileError && <Alert severity="error">{fileError}</Alert>}

          {fileErrors.length > 0 && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleDownloadErrors}>
                  Unduh Daftar Kesalahan
                </Button>
              }
            >
              <Stack spacing={0.5}>
                <span>
                  Ada {formatNumber(fileErrors.length)} baris bermasalah. Perbaiki
                  file lalu pilih ulang.
                </span>
                {fileErrors.slice(0, 5).map(({ row, sku, message }) => (
                  <span key={row}>
                    Baris {row}
                    {sku ? ` (${sku})` : ""}: {message}
                  </span>
                ))}
              </Stack>
            </Alert>
          )}

          {submitError && <Alert severity="error">{submitError}</Alert>}

          {merged && (
            <>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                <Chip
                  color={hasChanges ? "primary" : "default"}
                  label={`Harga berubah: ${formatNumber(merged.changed.length)} baris`}
                />
                <Chip
                  label={`Harga sama: ${formatNumber(merged.unchanged.length)} baris`}
                />
                {merged.notFound.length > 0 && (
                  <Chip
                    color="warning"
                    label={`SKU tidak ada di dokumen: ${formatNumber(merged.notFound.length)}`}
                  />
                )}
                {merged.stockOut.length > 0 && (
                  <Chip
                    color="warning"
                    label={`Pengurangan stok (diabaikan): ${formatNumber(merged.stockOut.length)}`}
                  />
                )}
              </Stack>

              {merged.notFound.length > 0 && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleDownloadErrors}
                    >
                      Unduh Daftar
                    </Button>
                  }
                >
                  SKU di file yang tidak ada di penyesuaian stok ini (diabaikan):{" "}
                  {merged.notFound
                    .slice(0, SHOWN_NOT_FOUND)
                    .map(({ sku }) => sku)
                    .join(", ")}
                  {merged.notFound.length > SHOWN_NOT_FOUND
                    ? `, dan ${formatNumber(merged.notFound.length - SHOWN_NOT_FOUND)} lainnya`
                    : ""}
                  .
                </Alert>
              )}

              {!hasChanges && (
                <Alert severity="info">
                  Tidak ada harga yang berbeda dari yang tersimpan, jadi tidak
                  ada yang perlu disimpan.
                </Alert>
              )}

              {hasChanges && (
                <>
                  <Alert severity="info">
                    Menyimpan akan memperbarui jurnal dan HPP pengiriman serta
                    faktur penjualan yang memakai stok ini. Untuk dokumen besar
                    ini bisa memakan waktu; jangan tutup halaman.
                  </Alert>
                  <Box sx={{ height: GRID_HEIGHT, width: "100%" }}>
                    <DataGridPro
                      rows={gridRows}
                      columns={columns}
                      rowHeight={44}
                      disableRowSelectionOnClick
                      hideFooterSelectedRowCount
                      localeText={{
                        noRowsLabel: "Tidak ada baris",
                        footerTotalRows: "Total baris:",
                      }}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Batal
        </Button>
        <Button
          variant="contained"
          loading={stage === "saving"}
          disabled={stage !== "ready" || !hasChanges}
          onClick={handleSave}
        >
          Simpan Perubahan Harga
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UpdateCostFromFileDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stockAdjustment: PropTypes.shape({
    referenceNumber: PropTypes.string,
    warehouseId: PropTypes.string,
    transactionDate: PropTypes.any,
    note: PropTypes.string,
    stockAdjustmentProductVariants: PropTypes.array,
  }),
  onUpdated: PropTypes.func.isRequired,
};
